import { cn } from "@omni/utils/styles";
import Tick from "@pqina/flip";
import { useEffect, useRef, useState } from "react";
import { VariantProps } from "tailwind-variants";
import { ReturnValue, useCountUp } from "use-count-up";

import { usePreviousAttributeValue } from "@/utils/attributes";

import { boxCounter } from "./boxCounter.styles";

type FlipProps = {
    value: string;
    length: number;
} & VariantProps<typeof boxCounter>;

type BoxCounterProps = {
    length: number;
    currentValue: number;
} & VariantProps<typeof boxCounter>;

const Flip = ({ value, textSize, length, theme }: FlipProps) => {
    const divRef = useRef<HTMLDivElement>(null);
    const tickRef = useRef<any>(null);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!divRef.current) return;

        Tick.DOM.create(divRef.current, {
            value,
            repeat: true,
            didInit: (tick: any) => {
                tickRef.current = tick;
            }
        });

        return () => {
            if (tickRef.current) {
                Tick.DOM.destroy(divRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (tickRef.current) {
            tickRef.current.value = value;
        }
    }, [value]);

    return (
        <div
            ref={divRef}
            style={{ "--box-text-length": length } as React.CSSProperties}
            className={cn(
                "tick",
                boxCounter({
                    textSize,
                    theme
                })
            )}
        >
            <div
                data-repeat="true"
                aria-hidden="true"
            >
                <span data-view="flip" />
            </div>
        </div>
    );
};

const BoxCounter = ({
    length,
    currentValue,
    textSize,
    theme
}: BoxCounterProps) => {
    const prevAmount = usePreviousAttributeValue({ currentValue });
    const [start, setStart] = useState(currentValue);
    const [end, setEnd] = useState(currentValue);

    const { value, reset } = useCountUp({
        isCounting: true,
        start: start,
        end: end
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        setEnd(currentValue);
        setStart(prevAmount?.currentValue || currentValue);
        reset();
    }, [currentValue]);

    const addLeadingZeros = (num: ReturnValue) =>
        String(num).padStart(length, "0");

    return (
        <Flip
            value={addLeadingZeros(value)}
            textSize={textSize}
            length={length}
            theme={theme}
        />
    );
};

export default BoxCounter;
